import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { buildTransactionKey, guid, TransactionKey } from '../../helper/order/buildTransactionKey';
import { DataContext } from '../../context/DataContext';
import { toast } from 'react-toastify';
import { config } from '../../config/Config';
import LoadingButton from '../../commonComponents/LoadingButton/LoadingButton';
import useEventEnter from '../../hooks/useEventEnter';

toast.configure();

const DriversList = (props) => {
  const { order_number, apiVersion, loadingConfig } = props;
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingAssign, setLoadingAssign] = useState(false);
  const [filterText, setFilterText] = useState('');
  const { profileAdmin } = useContext(DataContext);
  const number_request = useRef(guid());
  const [isRunningActionOrder, setIsRunningActionOrder] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  const getDrivers = async (page = 1, keyword = '', isLoadMore = false) => {
    try {
      // Cancel previous request if exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      if (!isMountedRef.current) return;

      if (!isLoadMore) {
        setIsLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params = {
        limit: 10,
        page,
        driverName: keyword,
      };

      const response = await axios.get(config.API_URL.LOCATION.DRIVER_ASSIGN_V3, {
        params,
        headers: config.headers,
        signal: abortControllerRef.current.signal,
      });

      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;

      const responseData = response.data || {};
      const newDrivers = responseData.drivers || [];
      const totalPages = responseData.totalPage || 1;
      const currentPageFromAPI = page;

      if (isLoadMore) {
        setData((prev) => [...prev, ...newDrivers]);
      } else {
        setData(newDrivers);
      }

      // Check if there are more pages based on API response
      setHasMore(currentPageFromAPI < totalPages);
    } catch (e) {
      // Don't log abort errors as they are intentional
      if (e.name !== 'AbortError' && e.name !== 'CanceledError') {
        console.log('Axios error: ', e);
      }
    } finally {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setIsLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const handleGetDataBySearch = () => {
    setCurrentPage(1);
    setHasMore(true);
    getDrivers(1, filterText, false);
  };

  const loadMoreDrivers = () => {
    if (hasMore && !loadingMore && !isLoading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      getDrivers(nextPage, filterText, true);
    }
  };

  useEventEnter(handleGetDataBySearch);

  useEffect(() => {
    getDrivers(1, '', false);
  }, []);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const assign = async (order_number, driver_id, name, token) => {
    if (isLoading || loadingConfig || loadingAssign) {
      return false;
    }
    if (isRunningActionOrder) return;
    setIsRunningActionOrder(true);
    setLoadingAssign(true);
    try {
      let apiConfig = null;
      if (apiVersion === 'v1') {
        apiConfig = config;
      } else {
        apiConfig = config;
      }
      await axios
        .patch(
          apiConfig.API_URL.ORDERS.ASSIGN_ORDER + order_number,
          { driver_id },
          {
            headers: {
              ...apiConfig.headers,
              ...buildTransactionKey(
                order_number,
                profileAdmin?._id,
                TransactionKey.execute,
                number_request.current
              ),
            },
          }
        )
        .then(async (response) => {
          if (response.data.status === 409) {
            toast.error(t('request_assigned_to_driver'), { position: toast.POSITION.BOTTOM_LEFT });
            props.closeModal();
          } else {
            if (props.refreshList) {
              props.refreshList();
            }
            toast(t('request_has_been_successfully_submitted'), {
              position: toast.POSITION.BOTTOM_LEFT,
            });
            props.closeModal();
          }
        })
        .finally(() => {
          setIsRunningActionOrder(false);
          number_request.current = guid();
        });
    } catch (err) {
      if (err?.response) {
        Object.entries(err?.response?.data)?.forEach(([key, error]) => {
          if (error?.message) {
            toast.error(error?.message, { position: toast.POSITION.BOTTOM_LEFT });
          }
        });
      }
    } finally {
      console.log(432);
      setLoadingAssign(false);
    }
  };

  return (
    <div className="DriversListContainer">
      {isLoading ? '' : ''}

      <div className="driversList rtl">
        <div className=" mb-8 flex flex-row h-14">
          <input
            type="text"
            name="search"
            className="w-5/6 bg-[#961d39]  pr-8 text-[#fff] mx-5 "
            autoComplete="off"
            placeholder={t('search_for_a_driver')}
            onChange={(e) => setFilterText(e.target.value)}
          />
          <LoadingButton onClick={handleGetDataBySearch} label={'Search'} loading={isLoading} />
        </div>
        <ul
          onScroll={(e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            if (scrollTop + clientHeight >= scrollHeight - 5) {
              loadMoreDrivers();
            }
          }}
          style={{ height: 'calc(100vh - 200px)', overflowY: 'auto' }}
        >
          {data.map((res) => {
            const driverName = res.driver?.name || 'Unknown Driver';

            return (
              <li key={res._id}>
                {driverName}
                <div
                  className="btnLeft flex space-x-2"
                  onClick={() => assign(order_number, res.driver_id, driverName, res.token)}
                >
                  {loadingAssign && (
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}
                  <span className="px-3"> {t('driver_list')}</span>
                </div>
              </li>
            );
          })}
          {loadingMore && (
            <li className="loading-more-item">
              <div className="text-center p-4">
                <div className="spinner-border" role="status">
                  <span className="sr-only">{t('loading')}</span>
                </div>
              </div>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default DriversList;
